import React, { useEffect } from "react";
import img from "../assets/Comunity.png";
import img2 from "../assets/live.webp";
import img3 from "../assets/parent.png";
import {
  BookOpen,
  Video,
  ClipboardCheck,
  Users,
  ArrowRight,
  ShieldCheck,
  Trophy,
  Headphones,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

type Feature = {
  icon: LucideIcon;
  title: string;
  desc: string;
  keywords: string;
};

type Step = {
  number: string;
  title: string;
  desc: string;
  keywords: string;
};

type TrustPoint = {
  icon: LucideIcon;
  title: string;
  desc: string;
  keywords: string;
};

const features: Feature[] = [
  {
    icon: Video,
    title: "Live Interactive Classes",
    desc: "Learn through live online classes led by expert teachers with real-time doubt solving, concept clarity and subject-wise guidance.",
    keywords:
      "live online classes, interactive classes for students, online tuition, expert teachers, live doubt solving, concept clarity classes",
  },
  {
    icon: ClipboardCheck,
    title: "Regular Tests and Practice",
    desc: "Improve exam readiness with chapter-wise tests, weekly practice, full syllabus tests, MCQ quizzes and performance-based reports.",
    keywords:
      "chapter wise tests, weekly practice test, full syllabus test series, MCQ quizzes, online exam practice, school test preparation",
  },
  {
    icon: Headphones,
    title: "Doubt Support",
    desc: "Students can clear Maths, Science, Physics, Chemistry, Biology, NCERT, JEE and NEET doubts through mentors and learning community support.",
    keywords:
      "student doubt support, Maths doubt solving, Science doubts, Physics numericals help, Chemistry concept support, Biology NCERT doubt solving",
  },
  {
    icon: Trophy,
    title: "Performance Tracking",
    desc: "Track marks, rank, attendance, course progress, test results and improvement areas through a smart student dashboard.",
    keywords:
      "student performance tracking, marks report, rank analysis, attendance tracking, course progress dashboard, test result analysis",
  },
];

const steps: Step[] = [
  {
    number: "01",
    title: "Choose Your Course",
    desc: "Select the right course for Class 7 to 12, CBSE, NCERT, school foundation, JEE Main, JEE Advanced or NEET UG preparation.",
    keywords:
      "choose online course, Class 7 to 12 courses, CBSE NCERT course, JEE Main course, NEET UG course, school foundation batch",
  },
  {
    number: "02",
    title: "Attend Live Classes",
    desc: "Join daily live classes, learn from experienced teachers and get direct academic guidance for your subject and exam goal.",
    keywords:
      "daily live classes, online learning classes, teacher guidance, JEE live classes, NEET live classes, CBSE online classes",
  },
  {
    number: "03",
    title: "Practice and Take Tests",
    desc: "Complete assignments, quizzes, mock tests, chapter tests and subject-wise practice to improve speed, accuracy and confidence.",
    keywords:
      "online assignments, student quizzes, mock tests, chapter tests, subject wise practice, speed and accuracy improvement",
  },
  {
    number: "04",
    title: "Track Your Growth",
    desc: "Use the dashboard to monitor performance, identify weak topics, review test reports and create a better study plan.",
    keywords:
      "track academic progress, weak topic analysis, student dashboard, test report review, study plan, progress improvement",
  },
];

const trustPoints: TrustPoint[] = [
  {
    icon: ShieldCheck,
    title: "Safe Learning Environment",
    desc: "A structured, focused and distraction-free online learning platform designed for school students and exam aspirants.",
    keywords:
      "safe online learning, distraction free learning platform, structured learning, focused study environment, student friendly LMS",
  },
  {
    icon: Users,
    title: "Mentor Guidance",
    desc: "Every student gets proper academic guidance, doubt support, preparation strategy and progress-based learning direction.",
    keywords:
      "mentor guidance for students, academic counseling, exam preparation strategy, personal study support, student mentorship",
  },
];

const homeExtraKeywords = [
  "Mediit Academy",
  "online learning platform for students",
  "best LMS for school students",
  "Class 7 online classes",
  "Class 8 online tuition",
  "Class 9 Maths Science coaching",
  "Class 10 CBSE board preparation",
  "Class 11 JEE NEET preparation",
  "Class 12 board exam preparation",
  "CBSE online classes",
  "NCERT online learning",
  "school foundation course",
  "JEE Main online coaching",
  "JEE Advanced preparation",
  "NEET UG online coaching",
  "Physics Chemistry Maths coaching",
  "Physics Chemistry Biology coaching",
  "live interactive classes",
  "online test series",
  "mock tests for students",
  "chapter wise practice tests",
  "student dashboard",
  "performance tracking",
  "doubt solving platform",
  "mentor guidance",
  "parent progress reports",
  "safe learning environment",
].join(", ");

const HomeExtraSections: React.FC = () => {
  useEffect(() => {
    const title =
      "Online Learning Platform for Class 7-12, JEE, NEET and CBSE Students | Mediit Academy";

    const description =
      "Mediit Academy offers live online classes, CBSE and NCERT preparation, Class 7 to 12 courses, JEE Main coaching, NEET UG preparation, doubt solving, mock tests, performance tracking and mentor guidance.";

    document.title = title;

    const setMetaTag = (name: string, content: string) => {
      let tag = document.querySelector(
        `meta[name="${name}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    const setPropertyTag = (property: string, content: string) => {
      let tag = document.querySelector(
        `meta[property="${property}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    setMetaTag("description", description);
    setMetaTag("keywords", homeExtraKeywords);
    setMetaTag("robots", "index, follow");
    setMetaTag("author", "Mediit Academy");

    setPropertyTag("og:title", title);
    setPropertyTag("og:description", description);
    setPropertyTag("og:type", "website");
    setPropertyTag("og:site_name", "Mediit Academy");

    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", title);
    setMetaTag("twitter:description", description);

    let canonical = document.querySelector(
      `link[rel="canonical"]`
    ) as HTMLLinkElement | null;

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute("href", window.location.origin);
  }, []);

  const homeExtraSchema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    name: "Mediit Academy",
    description:
      "Mediit Academy is an online learning platform for Class 7 to 12 students, CBSE and NCERT learners, JEE Main aspirants and NEET UG aspirants with live classes, doubt support, test series, performance tracking and mentor guidance.",
    url: "/",
    slogan:
      "Live online classes, test series, doubt solving and performance tracking for school, JEE and NEET students.",
    knowsAbout: [
      "Class 7 Online Classes",
      "Class 8 Online Tuition",
      "Class 9 Maths and Science",
      "Class 10 CBSE Board Preparation",
      "Class 11 JEE NEET Foundation",
      "Class 12 Board Exam Preparation",
      "JEE Main Online Coaching",
      "JEE Advanced Preparation",
      "NEET UG Online Coaching",
      "CBSE NCERT Learning",
      "Online Mock Tests",
      "Student Performance Tracking",
      "Doubt Solving Support",
      "Mentor Guidance",
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Learning Features and Student Support",
      itemListElement: features.map((feature, index) => ({
        "@type": "Offer",
        position: index + 1,
        itemOffered: {
          "@type": "Service",
          name: feature.title,
          description: feature.desc,
          keywords: feature.keywords,
        },
      })),
    },
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How Students Learn with Mediit Academy",
    description:
      "A simple learning process where students choose a course, attend live classes, practice through tests and track academic progress.",
    step: steps.map((step) => ({
      "@type": "HowToStep",
      name: step.title,
      text: step.desc,
    })),
  };

  return (
    <section
      className="bg-slate-50 font-['Inter']"
      aria-label="Mediit Academy online learning features, student dashboard and parent trust"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(homeExtraSchema),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(howToSchema),
        }}
      />

      {/* Why Choose Section - Image Right */}
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8">
        {/* Content Left */}
        <div className="order-1">
          <p className="text-sm font-black uppercase tracking-wide text-blue-600">
            Why Choose Mediit Academy
          </p>

          <h2 className="mt-3 text-3xl font-black leading-tight text-slate-950 sm:text-4xl lg:text-5xl">
            Smart Online Learning System for Serious Students
          </h2>

          <p className="mt-5 max-w-xl text-base leading-7 text-slate-600">
            Mediit Academy gives students a complete learning experience with
            live online classes, structured courses, regular tests, doubt
            support, mentor guidance and performance tracking on one simple LMS
            platform.
          </p>

          <p className="sr-only">{homeExtraKeywords}</p>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {features.map((item) => {
              const Icon = item.icon;

              return (
                <article
                  key={item.title}
                  title={item.keywords}
                  className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200 transition hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                    <Icon size={24} aria-hidden="true" />
                  </div>

                  <h3 className="font-black text-slate-950">{item.title}</h3>

                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    {item.desc}
                  </p>

                  <p className="sr-only">{item.keywords}</p>
                </article>
              );
            })}
          </div>
        </div>

        {/* Image Right */}
        <div className="relative order-2">
          <div className="absolute -left-6 -top-6 h-28 w-28 rounded-full bg-cyan-300/30 blur-2xl" />
          <div className="absolute -bottom-6 -right-6 h-32 w-32 rounded-full bg-blue-500/20 blur-2xl" />

          <div className="relative flex min-h-[300px] items-center justify-center overflow-hidden sm:min-h-[420px] lg:min-h-[500px]">
            <img
              src={img}
              alt="Mediit Academy online learning community for students with live classes doubt support and performance tracking"
              loading="lazy"
              decoding="async"
              className="h-auto max-h-[320px] w-full object-contain drop-shadow-2xl sm:max-h-[420px] lg:max-h-[500px]"
            />

            <div className="absolute bottom-4 left-4 right-4 rounded-3xl bg-white/90 p-4 shadow-xl backdrop-blur sm:bottom-8 sm:left-8 sm:right-8 sm:p-5">
              <div
                className="grid grid-cols-3 gap-3 text-center"
                aria-label="Mediit Academy student learning statistics"
              >
                <div>
                  <p className="text-base font-black text-slate-950 sm:text-xl">
                    25k+
                  </p>
                  <p className="text-[10px] font-bold text-slate-500 sm:text-xs">
                    Students
                  </p>
                </div>

                <div>
                  <p className="text-base font-black text-slate-950 sm:text-xl">
                    120+
                  </p>
                  <p className="text-[10px] font-bold text-slate-500 sm:text-xs">
                    Mentors
                  </p>
                </div>

                <div>
                  <p className="text-base font-black text-slate-950 sm:text-xl">
                    8.7k+
                  </p>
                  <p className="text-[10px] font-bold text-slate-500 sm:text-xs">
                    Doubts
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-black uppercase tracking-wide text-blue-600">
              Learning Process
            </p>

            <h2 className="mt-3 text-3xl font-black text-slate-950 sm:text-4xl">
              How Students Learn with Mediit Academy
            </h2>

            <p className="mt-4 text-base leading-7 text-slate-600">
              A simple and structured process that helps students join the right
              course, attend live classes, practice regularly and track academic
              growth with clear performance reports.
            </p>
          </div>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((step) => (
              <article
                key={step.number}
                title={step.keywords}
                className="relative overflow-hidden rounded-3xl bg-slate-50 p-6 ring-1 ring-slate-200 transition hover:-translate-y-1 hover:shadow-xl"
              >
                <span className="absolute right-5 top-4 text-5xl font-black text-slate-200">
                  {step.number}
                </span>

                <div className="relative">
                  <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-950 text-white">
                    <BookOpen size={22} aria-hidden="true" />
                  </div>

                  <h3 className="text-lg font-black text-slate-950">
                    {step.title}
                  </h3>

                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    {step.desc}
                  </p>

                  <p className="sr-only">{step.keywords}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>

      {/* App / Dashboard Section - Image Left */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#07111b] via-[#111936] to-[#1e3a5f] px-4 py-16 sm:px-6 lg:px-8">
        <div className="absolute -left-20 top-10 h-72 w-72 rounded-full bg-cyan-400/10 blur-3xl" />
        <div className="absolute -right-20 bottom-10 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-2">
          {/* Image Left */}
          <div className="relative order-2 lg:order-1">
            <div className="backdrop-blur">
              <img
                src={img2}
                alt="Student dashboard for live classes test results attendance assignments and progress tracking"
                loading="lazy"
                decoding="async"
                className="h-[260px] w-full rounded-[1.5rem] object-cover sm:h-[380px] lg:h-[460px]"
              />
            </div>
          </div>

          {/* Content Right */}
          <div className="order-1 lg:order-2">
            <p className="text-sm font-black uppercase tracking-wide text-cyan-300">
              Student Dashboard
            </p>

            <h2 className="mt-3 text-3xl font-black leading-tight text-white sm:text-4xl lg:text-5xl">
              Track Live Classes, Tests, Attendance and Progress in One Place
            </h2>

            <p className="mt-5 max-w-xl text-base leading-7 text-slate-300">
              The student dashboard helps learners track attendance, course
              progress, test results, assignments, upcoming live classes,
              completed lessons and improvement areas with a clean learning
              report.
            </p>

            <p className="sr-only">
              Student dashboard, LMS dashboard, attendance tracking, course
              progress tracking, test result report, assignment tracking,
              upcoming live classes, online learning analytics.
            </p>

            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              <div className="rounded-3xl border border-white/10 bg-white/10 p-5">
                <p className="text-2xl font-black text-white">95%</p>
                <p className="mt-1 text-sm font-medium text-slate-300">
                  Class Completion
                </p>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/10 p-5">
                <p className="text-2xl font-black text-white">4.8/5</p>
                <p className="mt-1 text-sm font-medium text-slate-300">
                  Student Rating
                </p>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/10 p-5">
                <p className="text-2xl font-black text-white">24/7</p>
                <p className="mt-1 text-sm font-medium text-slate-300">
                  Learning Access
                </p>
              </div>
            </div>

            <a
              href="/login"
              aria-label="View student dashboard for classes tests assignments attendance and progress tracking"
              className="mt-8 inline-flex items-center gap-2 rounded-2xl bg-cyan-400 px-6 py-4 text-sm font-black text-slate-950 shadow-lg shadow-cyan-500/20 transition hover:-translate-y-0.5 hover:bg-cyan-300"
            >
              View Dashboard
              <ArrowRight size={18} aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>

      {/* Trust Section - Image Right */}
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8">
        {/* Content Left */}
        <div className="order-1">
          <p className="text-sm font-black uppercase tracking-wide text-blue-600">
            Parent Trust and Student Safety
          </p>

          <h2 className="mt-3 text-3xl font-black leading-tight text-slate-950 sm:text-4xl lg:text-5xl">
            Built for Students, Trusted by Parents
          </h2>

          <p className="mt-5 max-w-xl text-base leading-7 text-slate-600">
            Parents can rely on transparent learning updates, progress reports,
            regular performance insights and a safe online education environment
            designed for focused academic growth.
          </p>

          <div className="mt-8 space-y-4">
            {trustPoints.map((point) => {
              const Icon = point.icon;

              return (
                <article
                  key={point.title}
                  title={point.keywords}
                  className="flex gap-4 rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200"
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                    <Icon size={24} aria-hidden="true" />
                  </div>

                  <div>
                    <h3 className="font-black text-slate-950">
                      {point.title}
                    </h3>

                    <p className="mt-1 text-sm leading-6 text-slate-600">
                      {point.desc}
                    </p>

                    <p className="sr-only">{point.keywords}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        {/* Image Right */}
        <div className="relative order-2">
          <div className="absolute -left-6 -top-6 h-28 w-28 rounded-full bg-orange-300/30 blur-2xl" />

          <div className="relative overflow-hidden ring-slate-200">
            <img
              src={img3}
              alt="Parents trust Mediit Academy for safe online learning mentor guidance and student progress reports"
              loading="lazy"
              decoding="async"
              className="h-[280px] w-full rounded-[1.5rem] object-cover sm:h-[380px] lg:h-[460px]"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeExtraSections;